import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  CircularProgress, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider, 
  Button,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, 
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, Area, AreaChart 
} from 'recharts';
import {
  TrendingUp,
  Description,
  Speed,
  HealthAndSafety,
  People,
  Storage,
  Memory,
  Computer,
  Search,
  Refresh,
  Assessment,
  Warning,
  CheckCircle,
  Error
} from '@mui/icons-material';
import AdminService from '../../services/AdminService';

// Enhanced color palette
const theme = {
  primary: '#1976d2',
  secondary: '#dc004e',
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  info: '#0288d1',
  gradient: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    success: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    warning: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    info: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  }
};

const COLORS = ['#667eea', '#764ba2', '#11998e', '#38ef7d', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [avgResponseTime, setAvgResponseTime] = useState(0);
  const [loadingResponseTime, setLoadingResponseTime] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const data = await AdminService.getDashboardStats();
        setStats(data);
        setError(null);
        setLastUpdated(new Date());
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    const fetchAverageResponseTime = async () => {
      try {
        setLoadingResponseTime(true);
        const avgTime = await AdminService.getAverageResponseTime('24h');
        setAvgResponseTime(avgTime);
      } catch (err) {
        console.error('Failed to fetch average response time:', err);
        // If there's an error, we'll use the mock data as fallback
      } finally {
        setLoadingResponseTime(false);
      }
    };

    fetchDashboardData();
    fetchAverageResponseTime();
    
    // Set up periodic refresh every 5 minutes
    const intervalId = setInterval(() => {
      fetchDashboardData();
      fetchAverageResponseTime();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    setLoadingResponseTime(true);
    
    try {
      const data = await AdminService.getDashboardStats();
      setStats(data);
      
      const avgTime = await AdminService.getAverageResponseTime('24h');
      setAvgResponseTime(avgTime);
      
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to refresh data:', err);
    } finally {
      setLoading(false);
      setLoadingResponseTime(false);
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="80vh"
        sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">Loading Dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="80vh"
        sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
      >
        <Error sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
        <Typography color="error" variant="h6" sx={{ mb: 2 }}>{error}</Typography>
        <Button 
          variant="contained" 
          startIcon={<Refresh />}
          onClick={() => window.location.reload()}
          sx={{ 
            background: theme.gradient.primary,
            '&:hover': { opacity: 0.9 }
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Enhanced mock data with more realistic metrics
  const mockStats = {
    userActivity: [
      { date: '2025-05-05', queries: 120, users: 45, satisfaction: 89 },
      { date: '2025-05-06', queries: 150, users: 50, satisfaction: 92 },
      { date: '2025-05-07', queries: 180, users: 65, satisfaction: 88 },
      { date: '2025-05-08', queries: 140, users: 52, satisfaction: 91 },
      { date: '2025-05-09', queries: 190, users: 68, satisfaction: 94 },
      { date: '2025-05-10', queries: 210, users: 72, satisfaction: 90 },
      { date: '2025-05-11', queries: 230, users: 85, satisfaction: 93 },
    ],
    documentStats: {
      totalDocuments: 356,
      docsByType: [
        { name: 'PDF', value: 145, color: COLORS[0] },
        { name: 'HTML', value: 87, color: COLORS[1] },
        { name: 'DOCX', value: 53, color: COLORS[2] },
        { name: 'XLSX', value: 29, color: COLORS[3] },
        { name: 'TXT', value: 42, color: COLORS[4] }
      ]
    },
    responseMetrics: {
      averageResponseTime: 2.3,
      accuracyScore: 87,
      satisfactionRate: 92,
      totalQueries: 1847,
      activeUsers: 342
    },
    topQueries: [
      { term: 'system architecture', count: 78, trend: '+12%' },
      { term: 'deployment guide', count: 65, trend: '+8%' },
      { term: 'security protocols', count: 59, trend: '+15%' },
      { term: 'API reference', count: 52, trend: '+5%' },
      { term: 'user authentication', count: 49, trend: '+20%' }
    ],
    systemHealth: {
      cpuUsage: 42,
      memoryUsage: 68,
      diskUsage: 56,
      status: 'Healthy',
      uptime: '23d 14h 32m',
      activeConnections: 127,
      responseTime: 145
    }
  };
  
  const displayStats = (!stats || !stats.documentStats) ? mockStats : stats;

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'healthy': return theme.success;
      case 'warning': return theme.warning;
      case 'critical': return theme.error;
      default: return theme.info;
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'healthy': return <CheckCircle />;
      case 'warning': return <Warning />;
      case 'critical': return <Error />;
      default: return <HealthAndSafety />;
    }
  };

  const MetricCard = ({ title, value, subtitle, icon, gradient, trend }) => (
    <Card 
      elevation={8}
      sx={{ 
        height: '100%',
        background: gradient,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          transition: 'transform 0.3s ease-in-out',
          boxShadow: '0 12px 20px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
            {icon}
          </Avatar>
          {trend && (
            <Chip 
              label={trend} 
              size="small" 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 'bold'
              }} 
            />
          )}
        </Box>
        <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
          {value}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          {subtitle}
        </Typography>
      </CardContent>
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          transform: 'translate(30px, -30px)',
          zIndex: 1
        }}
      />
    </Card>
  );

  const SystemHealthCard = ({ title, value, maxValue = 100, icon, status = 'normal' }) => {
    const percentage = (value / maxValue) * 100;
    const getColor = () => {
      if (percentage > 80) return theme.error;
      if (percentage > 60) return theme.warning;
      return theme.success;
    };

    return (
      <Card elevation={4} sx={{ p: 3, textAlign: 'center', height: '100%' }}>
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          <Avatar sx={{ bgcolor: `${getColor()}20`, color: getColor(), mr: 1 }}>
            {icon}
          </Avatar>
          <Typography variant="h6" color="text.primary">
            {title}
          </Typography>
        </Box>
        <Box position="relative" display="inline-flex" mb={2}>
          <CircularProgress 
            variant="determinate" 
            value={percentage} 
            size={80} 
            thickness={6} 
            sx={{ 
              color: getColor(),
              '& > circle': {
                strokeLinecap: 'round',
              }
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" component="div" fontWeight="bold">
              {`${Math.round(percentage)}%`}
            </Typography>
          </Box>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={percentage} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            bgcolor: `${getColor()}20`,
            '& .MuiLinearProgress-bar': {
              bgcolor: getColor(),
              borderRadius: 4,
            }
          }} 
        />
      </Card>
    );
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      pt: 3,
      pb: 4
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
              Analytics Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh}
              sx={{ 
                bgcolor: 'white',
                boxShadow: 2,
                '&:hover': { 
                  bgcolor: 'grey.100',
                  transform: 'rotate(180deg)',
                  transition: 'transform 0.5s ease-in-out'
                }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={3}>
          {/* Key Metrics Row */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Documents"
              value={(displayStats.documentStats && displayStats.documentStats.totalDocuments) ? 
                displayStats.documentStats.totalDocuments.toLocaleString() : '0'}
              subtitle="Indexed and searchable"
              icon={<Description />}
              gradient={theme.gradient.primary}
              trend="+12%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Avg Response Time"
              value={`${avgResponseTime.toFixed(2)}s`}
              subtitle="Last 24 hours"
              icon={<Speed />}
              gradient={theme.gradient.success}
              trend="-5%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Active Users"
              value={(displayStats.responseMetrics && displayStats.responseMetrics.activeUsers) ? 
                displayStats.responseMetrics.activeUsers.toLocaleString() : '0'}
              subtitle="Currently online"
              icon={<People />}
              gradient={theme.gradient.info}
              trend="+18%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={8}
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${getStatusColor(displayStats.systemHealth.status)} 0%, ${getStatusColor(displayStats.systemHealth.status)}aa 100%)`,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  transition: 'transform 0.3s ease-in-out'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    {getStatusIcon(displayStats.systemHealth.status)}
                  </Avatar>
                  <Chip 
                    label="LIVE" 
                    size="small" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      animation: 'pulse 2s infinite'
                    }} 
                  />
                </Box>
                <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                  {displayStats.systemHealth.status}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                  System Status
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Uptime: {displayStats.systemHealth.uptime}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* User Activity Chart */}
          <Grid item xs={12} lg={8}>
            <Card elevation={8} sx={{ p: 3, height: 400 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  User Activity & Satisfaction Trends
                </Typography>
                <Chip 
                  icon={<TrendingUp />} 
                  label="7 Days" 
                  variant="outlined" 
                  color="primary" 
                />
              </Box>
              <ResponsiveContainer width="100%" height="85%">
                <AreaChart data={displayStats.userActivity}>
                  <defs>
                    <linearGradient id="colorQueries" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#667eea" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#764ba2" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#764ba2" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="left" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <RechartsTooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="queries"
                    stroke="#667eea"
                    fillOpacity={1}
                    fill="url(#colorQueries)"
                    strokeWidth={3}
                    name="Queries"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="users"
                    stroke="#764ba2"
                    strokeWidth={3}
                    dot={{ fill: '#764ba2', strokeWidth: 2, r: 6 }}
                    name="Unique Users"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12} lg={4}>
            <Card elevation={8} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Quick Statistics
              </Typography>
              <Box display="flex" flexDirection="column" gap={3} mt={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 40, height: 40 }}>
                      <Search />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Queries
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {(displayStats.responseMetrics && displayStats.responseMetrics.totalQueries) ? 
                          displayStats.responseMetrics.totalQueries.toLocaleString() : '0'}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip label="+23%" color="success" size="small" />
                </Box>
                
                <Divider />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 40, height: 40 }}>
                      <Assessment />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Accuracy Score
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {displayStats.responseMetrics.accuracyScore}%
                      </Typography>
                    </Box>
                  </Box>
                  <Chip label="+2%" color="success" size="small" />
                </Box>
                
                <Divider />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 40, height: 40 }}>
                      <TrendingUp />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Satisfaction Rate
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {displayStats.responseMetrics.satisfactionRate}%
                      </Typography>
                    </Box>
                  </Box>
                  <Chip label="+5%" color="success" size="small" />
                </Box>
                
                <Divider />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'warning.main', mr: 2, width: 40, height: 40 }}>
                      <Speed />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Avg Response
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {displayStats.systemHealth.responseTime}ms
                      </Typography>
                    </Box>
                  </Box>
                  <Chip label="-12%" color="success" size="small" />
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Document Types */}
          <Grid item xs={12} md={6}>
            <Card elevation={8} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Document Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="85%">
                <PieChart>
                  <Pie
                    data={displayStats.documentStats.docsByType}
                    cx="50%"
                    cy="45%"
                    outerRadius={80}
                    innerRadius={40}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {displayStats.documentStats.docsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value) => [`${value} documents`, 'Count']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          {/* Top Queries */}
          <Grid item xs={12} md={6}>
            <Card elevation={8} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Popular Search Terms
              </Typography>
              <Box mt={2}>
                {displayStats.topQueries.map((query, index) => (
                  <Box key={index} mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" fontWeight="medium">
                        {query.term}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          {query.count}
                        </Typography>
                        <Chip 
                          label={query.trend} 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(query.count / displayStats.topQueries[0].count) * 100} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: `${COLORS[index % COLORS.length]}20`,
                        '& .MuiLinearProgress-bar': {
                          bgcolor: COLORS[index % COLORS.length],
                          borderRadius: 4,
                        }
                      }} 
                    />
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* System Health Metrics */}
          <Grid item xs={12}>
            <Card elevation={8} sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                System Performance Metrics
              </Typography>
              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <SystemHealthCard
                    title="CPU Usage"
                    value={displayStats.systemHealth.cpuUsage}
                    icon={<Computer />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SystemHealthCard
                    title="Memory Usage"
                    value={displayStats.systemHealth.memoryUsage}
                    icon={<Memory />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SystemHealthCard
                    title="Disk Usage"
                    value={displayStats.systemHealth.diskUsage}
                    icon={<Storage />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={4} sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Active Connections
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="primary" sx={{ my: 2 }}>
                      {displayStats.systemHealth.activeConnections}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current sessions
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Container>
      
      {/* Add CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default Dashboard;